/**
 * Initializes the application modules based on the configuration.
 *
 * This function iterates through the `MODULES` configuration,
 * which is an array of objects with `module` and `param` properties.
 * For each module, it checks if the corresponding
 * configuration parameter is truthy or set to `'NO_PARAM'`.
 * Configuration is obtained from store config trought api call (/api/v1/posweb/global_config)
 * If the condition is met, it dynamically imports the module
 * and calls its `init` function, passing the `app` instance.
 *
 * This function is responsible for bootstrapping the application's modules and ensuring
 * they are properly initialized based on the configuration.
 * es:
 *  param: 'ENDLESSAISLE_ENABLED',
    module: 'endless-aisle',
 */
import { MODULES } from '@/configs'
import { app } from '@/main'
import { FeLogger } from '@/addons/monitoring'
import { checkLegacyModuleConfig } from '@/abs-core/shared/lib/plugin-system'
import { GetStoreConfigDataAttributes } from '@/api'
import { init as initAddressValidation } from '@/modules/address-validation'
import store from '@/store'
import { ConfigGetters } from '@/store/configs-store'

function initModules() {
  MODULES.forEach(async (item) => {
    try {
      const { module, param } = item

      if (
        checkLegacyModuleConfig(param as keyof GetStoreConfigDataAttributes)
      ) {
        const moduleImport = await import(`@/modules/${module}/index.ts`)
        FeLogger.info(`[InitLegacyModules] Importing module: ${module}`)
        await moduleImport.init(app)
      }
    } catch (err) {
      FeLogger.error(
        `[InitLegacyModules] Error initializing module: ${item.module}`,
        err,
      )
    }
  })
}

// TODO: added manual init for address validation because state was used when adress validation feature is false
export function initLegacyModules(): void {
  initAddressValidation(app)
  // Init of modules postponed until store configs are loaded
  if (store.getters[ConfigGetters.GET_STORE_CONFIGS]) {
    initModules()
  }
  document.addEventListener('storeConfigsLoaded', initModules)
}
